import {useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import './IngestionDetail.scss';
import {
  IngestionDetailsType,
  IngestionProgress,
} from '../../../../api/types/ingestionApi.type';
import {useGetIngestionDetails} from '../../../../hooks/ingestions/useIngestions';
import IngestionProcessSection from './IngestionProcessSection';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrButton from '../../../common/arr-button/ArrButton';
import IngestionDetailsToolTip from './IngestionDetailsToolTip';
import dayjs from 'dayjs';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as CaretDown} from '../../../../assets/images/arrow-down-white.svg';
import {useNavigate} from 'react-router-dom';

interface IIngestionDetail {
  selectedIngestion: IngestionProgress;
  onRollbackClick: () => void;
  onCopyClick: () => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
  onApproveClick: () => void;
}

const IngestionDetail: React.FC<IIngestionDetail> = ({
  selectedIngestion,
  onRollbackClick,
  onDeleteClick,
  onApproveClick,
}) => {
  const [ingestionExecutionDetails, setIngestionExecutionDetails] =
    useState<IngestionDetailsType | null>(null);

  const {data, refetch} = useGetIngestionDetails({
    projectId: selectedIngestion?.projectId,
    ingestionId: selectedIngestion?.ingestionId,
  });
  // central ingestionName: prefer API data but fallback to selectedIngestion
  const [ingestionName, setIngestionName] = useState<string>(
    selectedIngestion?.name || ''
  );

  useEffect(() => {
    if (data) {
      setIngestionExecutionDetails(data);
      const nameFromResponse =
        data.ingestionDetailsModel?.ingestionName ||
        selectedIngestion?.name ||
        '';
      setIngestionName(nameFromResponse);
    }
  }, [data, selectedIngestion]);

  const ingestionModel = ingestionExecutionDetails?.ingestionDetailsModel;

  // ======== Details ========
  const ingestionDetails = [
    {
      label: t('ingestions.details.projectName'),
      value: ingestionModel?.projectName,
    },
    {
      label: t('ingestions.details.ingestionName'),
      value: ingestionModel?.ingestionName,
    },
    {
      label: t('ingestions.details.ingestionType'),
      value: ingestionModel?.ingestionType,
    },
    {
      label: t('ingestions.details.percentageCompleted'),
      value: ingestionModel?.status,
    },
    {
      label: t('ingestions.details.startDate'),
      value: ingestionModel?.startDate
        ? dayjs(ingestionModel.startDate).format('YYYY/MM/DD hh:mm:ss')
        : '',
    },
    {
      label: t('ingestions.details.endDate'),
      value: ingestionModel?.endDate
        ? dayjs(ingestionModel.endDate).format('YYYY/MM/DD hh:mm:ss')
        : '',
    },
    {
      label: t('ingestions.details.sourceLocation'),
      value: ingestionModel?.sourceLocation || '---',
    },
  ];

  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const onButtonClick1 = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };
  const onItemClick = (item: CustomMenuItem) => {
    item.onClick?.();
  };

  const navigate = useNavigate();

  const handleCopyMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    if (item && item.canCopy && item.ingestionId) {
      navigate('/Ingestion/Copy/' + item.ingestionId);
    }
  };

  const handleOpenWizardMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    if (item && item.canEdit && item.ingestionId) {
      navigate('/Ingestion/Edit/' + item.ingestionId);
    }
  };

  const onMenuItemClick = (selectedItem: string) => {
    switch (selectedItem) {
      case 'Open in Wizard':
        handleOpenWizardMenuItemClick(selectedIngestion);
        break;
      case t('ingestions.ingestionActions.rollback'):
        onRollbackClick();
        break;
      case 'Copy clicked':
        handleCopyMenuItemClick(selectedIngestion);
        break;
      case t('ingestions.ingestionActions.delete'):
        onDeleteClick();
        break;
      case t('ingestions.ingestionActions.approve'):
        onApproveClick();
        break;
      default:
        break;
    }
  };

  const getIngestionMenuItems = () => {
    return [
      {
        id: '1',
        label: 'Open in Wizard',
        onClick: () => onMenuItemClick('Open in Wizard'),
        disabled: !selectedIngestion?.canEdit,
      },
      {
        id: '2',
        label: t('ingestions.ingestionActions.rollback'),
        onClick: () =>
          onMenuItemClick(t('ingestions.ingestionActions.rollback')),
        disabled: !selectedIngestion?.canRollback,
      },
      {
        id: '3',
        label: 'Copy',
        onClick: () => onMenuItemClick('Copy clicked'),
        disabled: !selectedIngestion?.canCopy,
      },
      {
        id: '4',
        label: t('ingestions.ingestionActions.delete'),
        onClick: () => onMenuItemClick(t('ingestions.ingestionActions.delete')),
        disabled: !selectedIngestion?.canDelete,
      },
      {
        id: '5',
        label: t('ingestions.ingestionActions.approve'),
        onClick: () =>
          onMenuItemClick(t('ingestions.ingestionActions.approve')),
        disabled: !selectedIngestion?.canIndex,
      },
    ];
  };

  return (
    <Box className="ingestion-detail">
      {/* Header */}
      <Box className="ingestion-header">
        <Box display={'flex'}>
          <Box tabIndex={0} className="title">
            {t('ingestions.details.title')}
          </Box>
          <Box paddingLeft={1}>
            <ArrTooltipWrapper
              title={t('ingestions.details.title')}
              trigger={
                <InfoIcon
                  tabIndex={0}
                  aria-label={t('ingestions.details.title')}
                  className="info-icon"
                  fontSize="medium"
                />
              }
            >
              <IngestionDetailsToolTip />
            </ArrTooltipWrapper>
          </Box>
        </Box>
        {getIngestionMenuItems().length ? (
          <>
            <ArrMenu
              id="arr-menu-1"
              ref={menuRef1}
              items={getIngestionMenuItems()}
              onItemClick={onItemClick}
              direction="bottom-right"
              className="action-dropdown-menubox"
            />
            <ArrButton
              onClick={onButtonClick1}
              size="medium"
              aria-label={t('ingestions.details.action.name')}
              endIcon={<CaretDown />}
              dataTestId="ingestion-detail-name-button">
              {t('ingestions.details.action.name')}
            </ArrButton>
          </>
        ) : null}
      </Box>
      {/* Ingestion Summary */}
      <Box className="details-section">
        {ingestionDetails.map((detail, index) => (
          <Box key={index} className="detail-row">
            <Box className="detail-label">{detail.label}:</Box>
            <Box className="detail-value">{detail.value}</Box>
          </Box>
        ))}
      </Box>
      {/* Cataloging Section */}
      <IngestionProcessSection
        name="cataloging"
        projectId={selectedIngestion?.projectId}
        ingestionId={selectedIngestion?.ingestionId}
        ingestionName={ingestionName}
        ingestionState={ingestionExecutionDetails?.state || ''}
        title={t('ingestions.details.cataloging')}
        ingestionModel={ingestionModel}
        onIngestionProcessSuccess={() => refetch()}
        canReRun={ingestionExecutionDetails?.canCatalogReRun}
        canShowLabels={!ingestionModel?.cataloging.shouldDisplay}
      />
      {/* Copying Section */}
      <IngestionProcessSection
        name="copying"
        projectId={selectedIngestion?.projectId}
        ingestionId={selectedIngestion?.ingestionId}
        ingestionName={ingestionName}
        ingestionState={ingestionExecutionDetails?.state || ''}
        title={t('ingestions.details.copying')}
        ingestionModel={ingestionModel}
        onIngestionProcessSuccess={() => refetch()}
        canReRun={
          ingestionExecutionDetails?.canCopyingReRun ||
          ingestionExecutionDetails?.canCopying
        }
        canShowLabels={!ingestionModel?.copying.shouldDisplay}
      />
    </Box>
  );
};

export default IngestionDetail;
